import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Popover, PopoverWrapper } from '@dealstack/frontend/shared/components/Popover/Popover';
import React, { DOMAttributes } from 'react';
import styled, { css } from 'styled-components';

import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { IPopoverProps } from '@dealstack/frontend/shared/components/Popover/Popover.types';
import { Tooltip, TooltipIconWrapper } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import type { Align } from './Cell.types';

interface ICellProps {
	className?: string;
	children?: React.ReactNode;
	align?: Align;
	isInvalid?: boolean;
	onClick?: DOMAttributes<HTMLElement>['onClick'];
	popoverProps?: Omit<IPopoverProps, 'children'>;
	withPopover?: boolean;
	isNegative?: boolean;
}
export const Cell = styled(
	({ onClick, withPopover, popoverProps, className, children }: ICellProps) => {
		return (
			<div onClick={onClick} className={className}>
				{withPopover && popoverProps ? (
					<Popover {...popoverProps}>{children}</Popover>
				) : (
					children
				)}
			</div>
		);
	}
)`
	display: flex;
	align-items: center;

	${Popover}, ${PopoverWrapper} {
		display: flex;
		align-items: center;
		width: 100%;
	}

	&,
	${PopoverWrapper} {
		${({ align }) => getHorizontalAlign(align)};
	}

	${({ isInvalid, theme }) =>
		isInvalid ? `border: 1px dashed ${theme.v2.color.error[600]}` : ''};
`;

const HeaderSectionWrapper = styled.div``;
const HeaderGroupWrapper = styled.div``;
export const HeaderChildrenWrapper = styled.div`
	min-height: 1.25rem;
	display: flex;
	align-items: center;
`;

interface IHeaderCellProps extends ICellProps {
	children?: React.ReactNode;
	size?: number;
	customSize?: string;
	sectionLabel?: string;
	sectionIcon?: PredefinedIcons;
	sectionSeparator?: boolean;
	sectionComponent?: React.ReactNode;
	groupLabel?: string;
	groupComponent?: React.ReactNode;
	sectionAlign?: Align;
	groupAlign?: Align;
	childrenAlign?: Align;
	childrenTooltip?: string;
}

export const HeaderCell = styled(
	({
		sectionLabel,
		sectionComponent,
		groupLabel,
		groupComponent,
		childrenTooltip,
		sectionIcon,
		...props
	}: IHeaderCellProps) => {
		const { children } = props;
		const isChildrenText = typeof children === 'string';

		return (
			<Cell {...props}>
				{sectionLabel || sectionComponent ? (
					<HeaderSectionWrapper>
						{!!sectionIcon && <Icon variant={sectionIcon} size="lg" />}
						{sectionLabel ? (
							<Text noWrap variant="p-lg" bold>
								{sectionLabel}
							</Text>
						) : null}
						{sectionComponent}
					</HeaderSectionWrapper>
				) : null}
				{groupLabel || groupComponent ? (
					<HeaderGroupWrapper>
						{groupLabel ? (
							<Text noWrap variant="p-sm" bold>
								{groupLabel}
							</Text>
						) : null}
						{groupComponent}
					</HeaderGroupWrapper>
				) : null}

				{children ? (
					<HeaderChildrenWrapper>
						{isChildrenText ? (
							<Text
								colorFaded
								variant="overline"
								uppercase
								multiLineElipsis={{ numberOfLines: 2, enabled: true }}
							>
								{children}
								{!!childrenTooltip && <Tooltip tooltip={childrenTooltip} />}
							</Text>
						) : (
							children
						)}
					</HeaderChildrenWrapper>
				) : null}
			</Cell>
		);
	}
)`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;

	${Icon} {
		height: 100%;
	}

	${HeaderSectionWrapper} {
		display: flex;
		align-items: start;
		justify-content: center;
		gap: 0.5em;
		width: 100%;
		${({ sectionAlign }) => getHorizontalAlign(sectionAlign)}
	}
	${HeaderGroupWrapper} {
		height: 3rem;
		display: flex;
		align-items: center;
		width: 100%;
		${({ groupAlign }) => getHorizontalAlign(groupAlign)}
	}

	${HeaderChildrenWrapper} {
		height: 2rem;
		display: flex;
		width: 100%;
		${({ childrenAlign }) => getHorizontalAlign(childrenAlign)}
		/* Wrap at word boundaries; only break inside a word that cannot fit alone. */
		${Text} {
			word-break: normal;
			overflow-wrap: anywhere;
		}
		/* Fully inline so the icon flows after the label instead of forcing its own line. */
		${Tooltip}, ${TooltipIconWrapper} {
			text-transform: none;
			display: inline;
			vertical-align: middle;
		}
	}

	${({ sectionSeparator }) =>
		sectionSeparator
			? css`
					:before {
						position: absolute;
						content: '';
						width: 1px;
						height: 110%;
						background-color: ${({ theme }) => theme.v2.color.neutral[300]};
						top: 0;
						left: -1em;
					}
				`
			: ''}
`;
const getHorizontalAlign = (align?: Align) => {
	switch (align) {
		case 'center':
			return css`
				justify-content: center;
			`;
		case 'right':
			return css`
				justify-content: flex-end;
			`;

		default:
			return css`
				justify-content: flex-start;
			`;
	}
};
